<template ref="completePathway">
  <div v-if="!isLoading && pathway" class="complete-pathway-page">
    <Confetti v-if="showConfetti" ref="confetti" />
    <CompletePageModal :teamId="teamId" :pathwayId="pathway.id" ref="completeModal" />
    <router-link
      :to="cms.linkResolver(pathway)"
      class="back-button btn btn-default-back"
      target="_blank"
      v-if="!pathwayProgress?.completed"
      >Team Pathway
    </router-link>
    <h1 class="col-md-12 complete-page-message text-center">
      Congratulations! Your team has completed:
    </h1>
    <div v-if="outcome" class="col-md-12 outcome-title">
      <div class="outcome-title-text">
        <div class="outcome-title-header" v-html="cms.asHtml(outcome.full_title)"></div>
        <p class="outcome-title-desc" v-html="cms.asHtml(outcome.brief_description)"></p>
      </div>
      <div class="outcome-image-container">
        <img :src="outcome.image.url" :alt="cms.asText(outcome.name)" class="outcome-image" />
      </div>
    </div>
    <div class="complete-pathway-details">
      <h2><b>Pathway: </b>{{ cms.asText(pathway.data.title) }}</h2>
      <p
        v-html="cms.asHtml(pathway.data.pathway_complete_content)"
        class="pathway-complete-text"
      ></p>
      <div class="pathway-benefits">
        <h4 class="benefits-title">Benefits</h4>
        <ul class="benefits-list">
          <li
            v-for="(benefit, index) in pathway.data.benefits"
            :key="`benefit` + index"
            class="benefit-item"
          >
            <img src="../../assets/star-icon.svg" alt="bullet-point" class="star-icon" />
            <p>{{ cms.asText(benefit.benefit_description) }}</p>
          </li>
        </ul>
      </div>
    </div>
    <div class="complete-instructions">
      <p>{{ instructions }}</p>
    </div>
    <div class="button-container">
      <button
        class="btn btn-primary"
        @click="completePathway"
        data-test="complete-team-pathway-btn"
        type="button"
      >
        {{ buttonText }}
      </button>
      <button class="btn btn-default-celebrate" @click="toggleConfetti" type="button">
        Celebrate!
      </button>
    </div>
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { debug as d } from "debug";
import axios from "axios";
import { computed, onBeforeMount, ref } from "vue";
import { useRoute } from "vue-router";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";
import cms from "../../services/cms/cms";
import { CmsGoalData, CmsTeamPathway } from "../../services/cms/cms-type-definitions";
import CompletePageModal from "../../components/team-outcomes/complete-page-modal.vue";
import LoadingPage from "../loading-page.vue";
import Confetti from "../../components/lottie-confetti.vue";
import { csContext } from "../../services/cs-context";
import {
  TeamMemberViewedCompletePageApiModel,
  TeamMemberCompletedPathwayStepApiModel,
  TeamPathwayApiModel,
} from "../../api-models";

const debug = d("cs.CompletePathway");

const route = useRoute();

const isTeamLeader = ref(false);
const pathwayProgress = ref<TeamPathwayApiModel | null>(null);
const memberProgress = ref<TeamMemberCompletedPathwayStepApiModel | null>(null);
const pathway = ref<CmsTeamPathway | null>(null);
const outcome = ref<CmsGoalData | null>(null);
const outcomeId = ref("");
const teamId = ref("");
const isLoading = ref(true);
const showConfetti = ref(false);

const isTeamLeaderOrOrgAdmin = computed(
  () => isTeamLeader.value || csContext.userProfile.isOrgAdmin,
);
const buttonText = computed(() => (isTeamLeaderOrOrgAdmin.value ? "Complete Pathway" : "Continue"));
const instructions = computed(() => {
  const text = isTeamLeaderOrOrgAdmin.value
    ? `When you're ready, click the Pathway Complete button. Then choose a fresh Outcome or a different Pathway in this Outcome of '${cms.asText(
        outcome.value?.full_title,
      )}'.`
    : `Once your Team Lead has marked this Pathway as complete, choose a fresh Outcome or a different Pathway in this Outcome of '${cms.asText(
        outcome.value?.full_title,
      )}'.`;
  return `${text} See you in the next Pathway!`;
});

const completeModal = ref<InstanceType<typeof CompletePageModal> | null>(null);

async function completePathway(): Promise<void> {
  if (isTeamLeaderOrOrgAdmin.value) {
    completeModal.value?.openModal();
  } else {
    try {
      await axios.post("/api/TeamMemberCompletedPathway", {
        teamId: teamId.value,
        cmsPathwayId: pathway.value?.id,
      });
    } catch (e) {
      debug(e);
    } finally {
      window.location.href = `/home?view=TeamDashboard&team=${teamId.value}`;
    }
  }
}

async function getCurrentPathwayData(): Promise<void> {
  const response = await axios.get(`/api/TeamPathway/current/${teamId.value}`);
  const currentPathwayId = response.data;
  pathway.value = await cms.getById(CmsDocumentTypes.CmsTeamPathway, currentPathwayId);
  outcome.value = pathway.value.data.goal_relationship.data as CmsGoalData;
  const pathwayResponse = await axios.get(`/api/TeamPathway/${teamId.value}`);
  pathwayProgress.value = pathwayResponse.data.teamPathway;
  memberProgress.value = pathwayResponse.data.userProgress;
}

function toggleConfetti(): void {
  if (!showConfetti.value) {
    showConfetti.value = true;
    setTimeout(() => {
      showConfetti.value = false;
    }, 2000);
  }
}

async function hasViewedCompletePage(): Promise<void> {
  if (memberProgress.value && pathwayProgress.value?.completed) {
    const viewedPageModel: TeamMemberViewedCompletePageApiModel = {
      teamId: teamId.value,
      cmsStepId: memberProgress.value.cmsStepId,
      cmsPathwayId: memberProgress.value.cmsPathwayId,
      hasViewedCompletePage: true,
    };

    try {
      const response = await axios.patch("/api/TeamMemberCompletedPathwayStep", viewedPageModel);
      debug(response);
    } catch (e: any) {
      debug(e);
    }
  }
}

async function getTeamLeaderInfo(id: string): Promise<void> {
  const response = await axios.get(`/api/teamuserpermissions/${id}`);
  isTeamLeader.value = response.data.isLeader;
}

onBeforeMount(async () => {
  debug("PATHWAY");
  teamId.value = route.query.team?.toString() ?? "";
  await getCurrentPathwayData();
  await getTeamLeaderInfo(teamId.value);
  await hasViewedCompletePage();
  isLoading.value = false;
  toggleConfetti();
});
</script>

<style scoped lang="less">
@import "../../styles/outcomes/complete-pathway.less";
</style>

<!-- These styles are not scoped because they need to target elements produced by the CMS at runtime -->
<style lang="less">
@import "../../styles/outcomes/complete-pathway-cms-elements.less";
</style>
